import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { ArrowRight, Home, Key, TrendingUp, CalendarCheck, Quote } from "lucide-react";

import PeninsulaCarousel from "@/components/PeninsulaCarousel";
import heroImg from "@/assets/san-mateo-luxury-real-estate-hero.jpg";
import portrait from "@/assets/mark-tognoli-portrait.png";
import peninsulaMapBg from "@/assets/peninsula-map-bg.jpg";

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Mark Tognoli Realty"
        description="Luxury San Francisco Peninsula real estate. Homes for sale in Atherton, Burlingame, Hillsborough, Menlo Park, San Mateo, Woodside, Half Moon Bay & more. Mark Tognoli Realty — a decade of expertise."
        path="/"
      />
      <Navigation transparentOnTop />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        <img
          src={heroImg}
          alt="San Mateo County luxury homes for sale on the San Francisco Peninsula"
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 container mx-auto max-w-[1400px] px-6 pt-40 md:pt-44 pb-20 md:pb-28">
          <div className="max-w-6xl mx-auto text-center">
            <p className="eyebrow text-brand-ivory mb-6">San Mateo County · SF Peninsula</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] font-light text-white leading-[1.05] tracking-tight mb-6 md:mb-8 text-balance">
              Discover your dream home{" "}
              <span className="italic text-brand-ivory">in San Mateo County.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/95 font-light leading-relaxed max-w-xl mx-auto mb-10">
              Welcome to the Peninsula — a vibrant stretch between San Francisco and
              San Jose. As a premier Bay Area real estate expert, Mark Tognoli is
              dedicated to helping you find the perfect home in one of California's
              most dynamic and diverse communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/buy"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 sm:px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all"
              >
                Find Your Home <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/request-showing"
                className="inline-flex items-center gap-3 border border-white/40 text-white px-6 sm:px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-foreground transition-all"
              >
                <CalendarCheck className="h-4 w-4" /> Request a Showing
              </Link>
            </div>
            <div className="mt-4">
              <Link
                to="/consultation"
                className="inline-flex items-center gap-3 border border-white/40 text-white px-6 sm:px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-white hover:text-foreground transition-all"
              >
                <CalendarCheck className="h-4 w-4" /> Schedule Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / Philosophy */}
      <section className="py-20 md:py-32 texture-paper">
        <div className="container mx-auto max-w-[1100px] px-6 text-center">
          <p className="eyebrow mb-6">An Amazing Transaction</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight mb-8">
            Where every client is a priority,
            <span className="italic text-primary"> and every deal a milestone.</span>
          </h2>
          <div className="gold-divider mx-auto mb-8" />
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-light max-w-2xl mx-auto">
            We craft personalized real estate experiences for every client we
            represent. Understanding that no two journeys are alike, we take pride
            in offering high-touch, bespoke service — from first-time buyers in
            Belmont to generational sellers in Hillsborough. With our team, you
            are never another transaction; you are a valued member of our
            extended family.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-32 bg-secondary/40 border-y border-border texture-linen">
        <div className="container mx-auto max-w-[1400px] px-6">
          <div className="text-center mb-16 md:mb-20">
            <p className="eyebrow mb-4">Services</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground">
              Work with someone who has<br className="hidden md:block" />
              <span className="italic text-primary"> your best interest.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border">
            {[
              {
                icon: Home,
                title: "Residential Investments",
                desc: "From a first family home to a generational estate — meticulous representation across every price tier.",
              },
              {
                icon: TrendingUp,
                title: "Commercial Developments",
                desc: "Strategic counsel for investors, developers, and owners navigating the Bay Area's commercial landscape.",
              },
              {
                icon: Key,
                title: "Property Tax Savings",
                desc: "A complimentary annual review to ensure you never overpay — exclusive to Mark Tognoli Realty clients.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="bg-background p-12 hover:bg-card transition-colors duration-500 group"
              >
                <Icon className="h-8 w-8 text-primary mb-8 stroke-[1.25]" strokeWidth={1.25} />
                <h3 className="font-serif text-2xl font-light text-foreground mb-4">{title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial split — the team */}
      <section className="py-20 md:py-32 texture-paper">
        <div className="container mx-auto max-w-[1400px] px-6">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="relative">
              <div className="bg-secondary/60 aspect-[4/5] flex items-end justify-center overflow-hidden">
                <img
                  src={portrait}
                  alt="Mark Tognoli of Mark Tognoli Realty — luxury real estate expert serving Atherton, Burlingame, Hillsborough, Menlo Park, San Mateo and the SF Peninsula"
                  className="h-[88%] w-auto object-contain object-bottom"
                  width={800}
                  height={1000}
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-6 md:p-8 hidden md:block">
                <div className="text-3xl md:text-4xl font-serif font-light">10+</div>
                <div className="text-[10px] tracking-[0.3em] uppercase mt-1">Years Experience</div>
              </div>
            </div>
            <div>
              <p className="eyebrow mb-6">Meet Mark</p>
              <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight mb-8">
                Where integrity meets
                <span className="italic text-primary"> innovation.</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                A distinguished Realtor®, Mark Tognoli is known for
                robust client advocacy, superior customer service, and deep industry
                connections across the San Francisco Peninsula. He blends unwavering
                ethical standards with cutting-edge market strategies to redefine
                the modern real estate experience.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10">
                With more than a decade in the business, Mark delivers comprehensive
                marketing, negotiation, and sales strategies — earning a reputation
                for resilience, patience, and proactive follow-through with every
                buyer and seller he represents.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 text-primary text-xs tracking-[0.3em] uppercase border-b border-primary pb-2 hover:gap-5 transition-all"
              >
                Read Mark's Story <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="relative py-20 md:py-28 bg-secondary/40 border-t border-border overflow-hidden">
        <img
          src={peninsulaMapBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none"
          width={1920}
          height={1024}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 via-transparent to-secondary/60 pointer-events-none" />
        <div className="relative container mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">Areas We Serve</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground">
              The Peninsula, top to bottom.
            </h2>
          </div>
          <ul className="flex flex-wrap justify-center gap-x-8 sm:gap-x-10 gap-y-3 text-muted-foreground font-light">
            {[
              "Atherton","Belmont","Brisbane","Burlingame","Half Moon Bay",
              "Hillsborough","Menlo Park","Millbrae","Redwood City",
              "San Bruno","San Carlos","San Mateo","Woodside",
            ].map((city) => (
              <li key={city} className="text-base sm:text-lg">{city}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Lifestyle carousel */}
      <PeninsulaCarousel />

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-secondary/40 border-y border-border">
        <div className="container mx-auto max-w-[1200px] px-6">
          <div className="text-center mb-16 md:mb-20">
            <p className="eyebrow mb-4">Client Voices</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
              Treated like family,
              <span className="italic text-primary"> represented like royalty.</span>
            </h2>
            <div className="gold-divider mx-auto mt-8" />
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-border">
            {[
              {
                quote:
                  "From our first conversation, we felt like the only clients on their roster. Every detail was anticipated, every call returned within the hour. We closed above asking on a home we'd lost sleep over for months.",
                name: "Elena & David R.",
                role: "Buyers · Burlingame",
              },
              {
                quote:
                  "The marketing was magazine-quality and the negotiation was masterful. They sold our Hillsborough home for fourteen percent over the comp average — and made the entire process feel effortless.",
                name: "Margaret L.",
                role: "Seller · Hillsborough",
              },
              {
                quote:
                  "Three transactions in seven years. We have never once felt like a transaction ourselves. They are our trusted advisors for life — and now, family friends.",
                name: "The Chen Family",
                role: "Repeat Clients · San Mateo",
              },
            ].map((t) => (
              <figure
                key={t.name}
                className="bg-background p-10 md:p-12 flex flex-col"
              >
                <Quote className="h-8 w-8 text-primary mb-6" strokeWidth={1.25} />
                <blockquote className="text-foreground font-light leading-relaxed text-base md:text-lg mb-8 flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption>
                  <div className="font-serif text-lg text-foreground">{t.name}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">
                    {t.role}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-brand-charcoal text-brand-ivory">
        <div className="container mx-auto max-w-[900px] px-6 text-center">
          <p className="eyebrow text-primary mb-6">Find Your Dream Home</p>
          <h2 className="font-serif text-3xl md:text-6xl font-light leading-tight mb-8">
            Embark on your real estate journey
            <span className="italic"> with Mark.</span>
          </h2>
          <p className="text-brand-ivory/90 font-light text-base md:text-lg mb-10 leading-relaxed">
            You're not just another transaction — you're a valued member of our
            real estate family.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/request-showing"
              className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase hover:bg-primary/90 transition-all"
            >
              <CalendarCheck className="h-4 w-4" /> Request a Showing
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-ivory/30 text-brand-ivory px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase hover:bg-brand-ivory hover:text-foreground transition-all"
            >
              Full Contact <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
